import {
  convertToRaw,
  EditorState,
  RawDraftContentBlock,
  RawDraftContentState,
  RawDraftInlineStyleRange,
} from "draft-js";
import { draftToMarkdown } from "markdown-draft-js";
import { IMember } from "src/app/project/ProjectTypes";
import { containsEmoji, stripCharacters } from "src/utils/methods";
import { EntityRanges } from "./TextBox";

const TAG_STYLE_NAMES = {
  b: "BOLD",
  i: "ITALIC",
  u: "UNDERLINE",
};

function getCodedMentions(content: string): string {
  const spanRegex =
    /<span[^>]*\bdata-mention\s*=\s*["']([^"']+)["'][^>]*>[\s\S]*?<\/span>/gi;

  return content.replace(spanRegex, function (_match, mentionId) {
    return `[wsMember:${mentionId}]`;
  });
}

function getCodedVariables(content: string): string {
  const spanRegex =
    /<span[^>]*\bdata-variable\s*=\s*["']([^"']+)["'][^>]*>[\s\S]*?<\/span>/gi;

  return content.replace(spanRegex, function (_match, variableId) {
    return `[${variableId}]`;
  });
}

function getCodedLinks(content: string): string {
  const linkRegex =
    /<a[^>]*\bhref\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;

  return content.replace(linkRegex, function (_match, url, text) {
    // Encode link as [wsLink|URL|TEXT] using | as separator
    // This handles both custom links (text ≠ url) and auto-detected links (text = url)
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);
    return `[wsLink|${encodedUrl}|${encodedText}]`;
  });
}

const getCodedSpanTags = (content: string) => {
  const codedMentions = getCodedMentions(content);
  const codedVariables = getCodedVariables(codedMentions);
  const codedLinks = getCodedLinks(codedVariables);

  return codedLinks;
};

function getDecodedMentions(text: string, membersList: IMember[]) {
  const bracketRegex =
    /\[\s*wsMember:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\s*\]/g;

  return text.replace(bracketRegex, function (match) {
    const memberId = stripCharacters(match, ["[wsMember:", "]"]);
    const member = membersList.find((item) => item.id === memberId);
    // If member not found, return the original match to avoid undefined in output
    if (!member) {
      return match;
    }
    return `<span data-mention="${memberId}">@${member.name}</span>`;
  });
}

function getDecodedVariables(text: string, variablesList: any[]) {
  const bracketRegex =
    /\[\s*wsMetaFieldUuid:([^:\]\s]+):([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\s*\]/g;

  return text.replace(bracketRegex, function (match) {
    const variableId = stripCharacters(match, ["[", "]"]);
    const variable = variablesList.find((item) => item.id === variableId);
    // If variable not found, return the original match to avoid undefined in output
    if (!variable) {
      return match;
    }
    return `<span data-variable="${variableId}">${variable.name}</span>`;
  });
}

// Helper function to decode and sanitize wsLink format
function decodeWsLink(
  encodedUrl: string,
  encodedText: string,
): { url: string; text: string } | null {
  try {
    const url = decodeURIComponent(encodedUrl);
    const linkText = decodeURIComponent(encodedText);

    // Sanitize URL and text to prevent XSS
    const sanitizedUrl = url.replace(/["'<>]/g, "");
    const sanitizedText = linkText.replace(/[<>]/g, "");

    return { url: sanitizedUrl, text: sanitizedText };
  } catch (e) {
    console.error("Failed to decode link:", encodedUrl, encodedText, e);
    return null;
  }
}

function getDecodedLinks(text: string): string {
  // Match [wsLink|ENCODED_URL|ENCODED_TEXT] using | as separator
  // Use non-greedy match and proper escaping
  const bracketRegex = /\[wsLink\|([^|]+)\|([^\]]+)\]/g;

  return text.replace(bracketRegex, function (_match, encodedUrl, encodedText) {
    const decoded = decodeWsLink(encodedUrl, encodedText);
    if (!decoded) {
      // If decoding fails, return the original match to preserve data
      return _match;
    }
    return `<a href="${decoded.url}">${decoded.text}</a>`;
  });
}

const getDecodedSpanTags = (
  content: string,
  membersList: IMember[],
  variablesList: any[],
) => {
  const decodedMentions = getDecodedMentions(content, membersList);
  const decodedVariables = getDecodedVariables(decodedMentions, variablesList);
  const decodedLinks = getDecodedLinks(decodedVariables);

  return decodedLinks;
};

export const getTextWithEncodedMentions = (value: EditorState) => {
  const content = value.getCurrentContent();
  const rawObject = convertToRaw(content);
  const markdownString = draftToMarkdown(rawObject, {
    entityItems: {
      mention: {
        open: (entity: any) => {
          return `<span data-mention="${entity.data.mention.id}">`;
        },
        close: () => {
          return "</span>";
        },
      },
      "{{mention": {
        open: (entity: any) => {
          return `<span data-variable="${entity.data.mention.id}">`;
        },
        close: () => {
          return `</span>`;
        },
      },
      emoji: {
        open: (entity: any) => {
          return `<span data-emoji="${entity.data.emojiUnicode}">`;
        },
        close: () => {
          return `</span>`;
        },
      },
      LINK: {
        open: (entity: any) => {
          return `<a href="${entity.data.url || entity.data.href || ""}">`;
        },
        close: () => {
          return `</a>`;
        },
      },
      link: {
        open: (entity: any) => {
          return `<a href="${entity.data.url || entity.data.href || ""}">`;
        },
        close: () => {
          return `</a>`;
        },
      },
    },
    styleItems: {
      BOLD: {
        open: () => "<b>",
        close: () => "</b>",
      },
      ITALIC: {
        open: () => "<i>",
        close: () => "</i>",
      },
      UNDERLINE: {
        open: () => "<u>",
        close: () => "</u>",
      },
    },
    preserveNewlines: true,
  });

  return getCodedSpanTags(markdownString);
};

export const getMentionsWithPositions = (
  value: string,
  membersList: IMember[],
  variablesList: any[],
): RawDraftContentState => {
  const defaultState = {
    blocks: [
      {
        key: "ddik6",
        text: value,
        type: "unstyled",
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {},
      },
    ],
    entityMap: {},
  };

  if (!value.length) return defaultState;

  const startTime = Date.now();
  const MAX_PROCESSING_TIME = 2000; // 2 seconds max

  const content = getDecodedSpanTags(value, membersList, variablesList);

  // Check if decoding took too long
  if (Date.now() - startTime > MAX_PROCESSING_TIME) {
    console.error(
      "getMentionsWithPositions: Decoding timeout, returning plain text",
    );
    return defaultState;
  }

  const lines = content?.split("\n");
  const blocks: Array<RawDraftContentBlock> = [];
  let entityMap = {};
  let entityRangesKey = 0;

  lines.forEach((line, idx) => {
    // Check global timeout
    if (Date.now() - startTime > MAX_PROCESSING_TIME) {
      console.error(
        "getMentionsWithPositions: Global timeout, adding remaining lines as plain text",
      );
      blocks.push({
        key: `bis6${idx}`,
        text: line || "",
        type: "unstyled",
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {},
      });
      return;
    }

    const text = line;

    // Simplified regex without catastrophic backtracking - MUCH faster
    // Match: opening/closing tags OR text content (anything that's not <)
    // Note: This doesn't handle standalone < in text, but that's rare and acceptable tradeoff
    const tagPattern = /<(\/?)(b|i|u|span|a)([^>]*)>|([^<]+)/g;

    const entityRanges: Array<EntityRanges> = [];
    const inlineStyleRanges: Array<RawDraftInlineStyleRange> = [];
    const stack: { tag: string; start: number; attr?: string }[] = [];
    let resultText = "";
    let currentOffset = 0;
    let match: RegExpExecArray | null;
    let lastIndex = 0;
    let iterationCount = 0;

    while ((match = tagPattern.exec(text)) !== null) {
      // Check timeout every 100 iterations
      if (
        ++iterationCount % 100 === 0 &&
        Date.now() - startTime > MAX_PROCESSING_TIME
      ) {
        console.error(
          "getMentionsWithPositions: Timeout in regex loop, adding remaining text",
        );
        resultText += text.substring(tagPattern.lastIndex);
        break;
      }

      // Prevent infinite loop: if regex doesn't advance position or matched empty string, force it forward
      if (tagPattern.lastIndex === lastIndex || match[0].length === 0) {
        tagPattern.lastIndex = lastIndex + 1;
        if (tagPattern.lastIndex > text.length) {
          break;
        }
        continue;
      }
      lastIndex = tagPattern.lastIndex;

      if (match[4]) {
        const text = match[4];
        resultText += text;
        if (containsEmoji(text)) {
          currentOffset += 1;
        } else {
          currentOffset += text.length;
        }
        continue;
      }

      const isClosing = match[1] === "/";
      const tag = match[2];
      const attrs = match[3] || "";

      if (!isClosing) {
        // Opening tag
        if (tag === "span") {
          const variable = /data-variable="([^"]+)"/.exec(attrs);
          const mention = /data-mention="([^"]+)"/.exec(attrs);
          const emoji = /data-emoji="([^"]+)"/.exec(attrs);
          if (variable) {
            stack.push({
              tag: "span-variable",
              start: currentOffset,
              attr: variable[1],
            });
          } else if (mention) {
            stack.push({
              tag: "span-mention",
              start: currentOffset,
              attr: mention[1],
            });
          } else if (emoji) {
            stack.push({
              tag: "span-emoji",
              start: currentOffset,
              attr: emoji[1],
            });
          } else {
            stack.push({ tag, start: currentOffset });
          }
        } else if (tag === "a") {
          const href = /href="([^"]+)"/.exec(attrs);
          if (href) {
            stack.push({
              tag: "a",
              start: currentOffset,
              attr: href[1],
            });
          } else {
            stack.push({ tag, start: currentOffset });
          }
        } else {
          stack.push({ tag, start: currentOffset });
        }
      } else {
        // Closing tag
        const idx = [...stack]
          .reverse()
          .findIndex((s) => s.tag === tag || s.tag.startsWith(`span-`));
        if (idx !== -1) {
          const realIndex = stack.length - 1 - idx;
          const open = stack.splice(realIndex, 1)[0];
          const length = currentOffset - open.start;

          const SUPPORTED_TAGS = ["b", "i", "u"];
          const SUPPORTED_ATTRS = ["span-variable", "span-mention"];

          if (SUPPORTED_TAGS.includes(open.tag)) {
            inlineStyleRanges.push({
              // @ts-ignore
              style: TAG_STYLE_NAMES[open.tag],
              offset: open.start,
              length,
            });
          } else if (open.tag === "a" && open.attr) {
            entityRanges.push({
              key: entityRangesKey,
              offset: open.start,
              length,
            });
            entityRangesKey += 1;

            const entityElementsCount = Object.keys(entityMap).length;
            entityMap = {
              ...entityMap,
              [entityElementsCount]: {
                type: "LINK",
                mutability: "MUTABLE",
                data: {
                  url: open.attr,
                },
              },
            };
          } else if (open.tag === "span-emoji") {
            entityRanges.push({
              key: entityRangesKey,
              offset: open.start,
              length: 1,
            });
            entityRangesKey += 1;

            const entityElementsCount = Object.keys(entityMap).length;
            entityMap = {
              ...entityMap,
              [entityElementsCount]: {
                type: "emoji",
                mutability: "IMMUTABLE",
                data: {
                  emojiUnicode: open.attr,
                },
              },
            };
          } else if (SUPPORTED_ATTRS.includes(open.tag) && open.attr) {
            const getData = () => {
              if (open.tag === "span-variable") {
                return {
                  list: variablesList,
                  type: "{{mention",
                };
              }

              return {
                list: membersList,
                type: "mention",
              };
            };

            entityRanges.push({
              key: entityRangesKey,
              offset: open.start,
              length,
            });
            entityRangesKey += 1;

            const entityElementsCount = Object.keys(entityMap).length;

            const { list, type } = getData();

            const object = list?.find((item) => {
              return item.id === open.attr;
            });

            if (object) {
              entityMap = {
                ...entityMap,
                [entityElementsCount]: {
                  type,
                  mutability: "IMMUTABLE",
                  data: {
                    mention: {
                      id: object.id,
                      name: object.name,
                      email: object.email,
                      avatar: object.avatarUrl,
                    },
                  },
                },
              };
            }
          }
        }
      }
    }

    blocks.push({
      key: `bis6${idx}`,
      text: resultText || "",
      type: "unstyled",
      depth: 0,
      inlineStyleRanges,
      entityRanges,
      data: {},
    });
  });

  return { blocks, entityMap };
};

export const replaceHTMLTagsWithSpan = (value: string, tags: string[]) => {
  let newValue = value;

  tags.forEach((tag) => {
    newValue = newValue
      .replaceAll(`<${tag}`, "<span")
      .replaceAll(`</${tag}>`, "</span>");
  });

  return newValue;
};
